import {
  ETIQUETA_ESTADO_BB,
  ETIQUETA_ESTADO_BB_EN,
  ETIQUETA_LINEA,
  ETIQUETA_LINEA_EN,
  type BigBagResumen,
  type OrdenInventario,
} from '@cnc/contracts';
import {et, tr, txt, type Texto} from '../i18n/idioma';
import {fecha, mes, numero, textoOGuion} from '../utilidades/formato';
import {ayudaDeBigBag} from './ayuda-inventario';
import {type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';

// Grupos de columnas. Se usa el mismo objeto en la lista de grupos y en cada
// columna; su llave estable es el texto en espanol (claveDeTexto).
export const G = {
  principal: txt('Principal', 'Main'),
  pesos: txt('Pesos y tenores', 'Weights and grades'),
  origen: txt('Origen', 'Origin'),
  fechas: txt('Fechas', 'Dates'),
  archivo: txt('Columnas del archivo', 'File columns'),
};

export type GrupoDeColumna = Texto;

export type ColumnaDeInventario = ColumnaDeTabla<BigBagResumen, OrdenInventario> & {grupo: GrupoDeColumna};

const extra =
  (clave: string, formato: (v: string | number) => string = String) =>
  (b: BigBagResumen) => {
    const v = b.extra[clave];
    return v === null || v === undefined || v === '' ? '—' : formato(v);
  };

// Las 53 columnas elegibles de la app original, agrupadas. Las principales
// vienen marcadas por defecto. Cada una lleva su nota (i): que es y como se llena.
export const COLUMNAS_DE_INVENTARIO: ColumnaDeInventario[] = (
  [
    {id: 'bbNo', titulo: 'BB #', grupo: G.principal, orden: 'bbNo', porDefecto: true, ancho: 120, valor: b => b.bbNo},
    {
      id: 'estado',
      titulo: txt('Estado', 'Status'),
      grupo: G.principal,
      orden: 'estado',
      porDefecto: true,
      ancho: 110,
      valor: b => et(ETIQUETA_ESTADO_BB, ETIQUETA_ESTADO_BB_EN)[b.estado],
    },
    {
      id: 'linea',
      titulo: txt('Inventario', 'Inventory'),
      grupo: G.principal,
      ancho: 110,
      valor: b => et(ETIQUETA_LINEA, ETIQUETA_LINEA_EN)[b.linea],
    },
    {
      id: 'exportacion',
      titulo: txt('Exportación', 'Export'),
      grupo: G.principal,
      orden: 'exportacion',
      porDefecto: true,
      ancho: 130,
      valor: b => textoOGuion(b.exportacion),
    },
    {
      id: 'recoleccion',
      titulo: txt('Recolección', 'Pickup'),
      grupo: G.principal,
      orden: 'recoleccion',
      porDefecto: true,
      ancho: 110,
      valor: b => (b.envioDirecto ? tr('Directo', 'Direct') : textoOGuion(b.recoleccion)),
    },
    {
      id: 'contenedor',
      titulo: txt('Contenedor', 'Container'),
      grupo: G.principal,
      orden: 'contenedor',
      ancho: 130,
      valor: b => textoOGuion(b.contenedor),
    },
    {
      id: 'fechaEmpaque',
      titulo: txt('Fecha empaque', 'Packing date'),
      grupo: G.fechas,
      orden: 'fechaEmpaque',
      porDefecto: true,
      ancho: 120,
      valor: b => fecha(b.fechaEmpaque),
    },
    {
      id: 'sello',
      titulo: txt('Sello', 'Seal'),
      grupo: G.principal,
      orden: 'sello',
      porDefecto: true,
      ancho: 110,
      valor: b => textoOGuion(b.sello),
    },
    {
      id: 'tipoMaterial',
      titulo: txt('Tipo', 'Type'),
      grupo: G.principal,
      orden: 'tipoMaterial',
      porDefecto: true,
      ancho: 80,
      valor: b => textoOGuion(b.tipoMaterial),
    },
    {
      id: 'tonHumedas',
      titulo: txt('Ton. húmedas', 'Wet tons'),
      grupo: G.pesos,
      orden: 'tonHumedas',
      numerica: true,
      porDefecto: true,
      ancho: 110,
      valor: b => numero(b.tonHumedas, 3),
    },
    {
      id: 'tonSecas',
      titulo: txt('Ton. secas', 'Dry tons'),
      grupo: G.pesos,
      orden: 'tonSecas',
      numerica: true,
      porDefecto: true,
      ancho: 110,
      valor: b => numero(b.tonSecas, 3),
    },
    {
      id: 'auGt',
      titulo: 'Au (g/t)',
      grupo: G.pesos,
      orden: 'auGt',
      numerica: true,
      porDefecto: true,
      ancho: 100,
      valor: b => numero(b.auGt, 2),
    },
    {
      id: 'auG',
      titulo: 'Au (g)',
      grupo: G.pesos,
      orden: 'auG',
      numerica: true,
      porDefecto: true,
      ancho: 110,
      valor: b => numero(b.auG, 2),
    },
    {
      id: 'agGt',
      titulo: 'Ag (g/t)',
      grupo: G.pesos,
      orden: 'agGt',
      numerica: true,
      porDefecto: true,
      ancho: 100,
      valor: b => numero(b.agGt, 2),
    },
    {
      id: 'agG',
      titulo: 'Ag (g)',
      grupo: G.pesos,
      orden: 'agG',
      numerica: true,
      porDefecto: true,
      ancho: 110,
      valor: b => numero(b.agG, 2),
    },
    {
      id: 'auOz',
      titulo: 'Au (oz)',
      grupo: G.pesos,
      orden: 'auOz',
      numerica: true,
      ancho: 100,
      valor: b => numero(b.auOz, 3),
    },
    {
      id: 'agOz',
      titulo: 'Ag (oz)',
      grupo: G.pesos,
      orden: 'agOz',
      numerica: true,
      ancho: 100,
      valor: b => numero(b.agOz, 3),
    },
    {
      id: 'humedad',
      titulo: txt('Humedad %', 'Moisture %'),
      grupo: G.pesos,
      orden: 'humedad',
      numerica: true,
      porDefecto: true,
      ancho: 100,
      valor: b => numero(b.humedadPorcentaje, 2),
    },
    {
      id: 'mesMolienda',
      titulo: txt('Mes molienda', 'Grinding month'),
      grupo: G.fechas,
      orden: 'mesMolienda',
      porDefecto: true,
      ancho: 110,
      valor: b => mes(b.mesMolienda),
    },
    {
      id: 'mesMoliendaReal',
      titulo: txt('Mes molienda real', 'Real grinding month'),
      grupo: G.fechas,
      ancho: 130,
      valor: b => mes(b.mesMoliendaReal),
    },
    {
      id: 'etd',
      titulo: txt('Zarpe (ETD)', 'Departure (ETD)'),
      grupo: G.fechas,
      orden: 'etd',
      ancho: 110,
      valor: b => fecha(b.etd),
    },
    {
      id: 'eta',
      titulo: txt('Llegada (ETA)', 'Arrival (ETA)'),
      grupo: G.fechas,
      orden: 'eta',
      ancho: 110,
      valor: b => fecha(b.eta),
    },
    {
      id: 'mesMoliendaReportado',
      titulo: txt('Mes molienda (archivo)', 'Grinding month (file)'),
      grupo: G.fechas,
      ancho: 140,
      valor: b => mes(b.mesMoliendaReportado),
    },
    {
      id: 'mina',
      titulo: txt('Mina', 'Mine'),
      grupo: G.origen,
      orden: 'mina',
      porDefecto: true,
      ancho: 130,
      valor: b => textoOGuion(b.mina),
    },
    {
      id: 'proveedor',
      titulo: txt('Proveedor', 'Supplier'),
      grupo: G.origen,
      orden: 'proveedor',
      porDefecto: true,
      ancho: 130,
      valor: b => textoOGuion(b.proveedor),
    },
    {
      id: 'exportador',
      titulo: txt('Exportador', 'Exporter'),
      grupo: G.origen,
      orden: 'exportador',
      porDefecto: true,
      ancho: 130,
      valor: b => textoOGuion(b.exportador),
    },
    {
      id: 'planta',
      titulo: txt('Planta', 'Plant'),
      grupo: G.origen,
      orden: 'planta',
      porDefecto: true,
      ancho: 130,
      valor: b => textoOGuion(b.planta),
    },
    {
      id: 'ubicacion',
      titulo: txt('Bodega', 'Warehouse'),
      grupo: G.origen,
      orden: 'ubicacion',
      porDefecto: true,
      ancho: 140,
      valor: b => textoOGuion(b.ubicacion),
    },
    {
      id: 'municipio',
      titulo: txt('Municipio', 'Municipality'),
      grupo: G.origen,
      orden: 'municipio',
      ancho: 120,
      valor: b => textoOGuion(b.municipio),
    },
    {
      id: 'entradaBodega',
      titulo: txt('Entrada a bodega', 'Warehouse entry'),
      grupo: G.fechas,
      orden: 'entradaBodega',
      ancho: 130,
      valor: b => fecha(b.entradaBodega),
    },
    {
      id: 'fechaLlenado',
      titulo: txt('Día de llenado', 'Filling date'),
      grupo: G.fechas,
      ancho: 120,
      valor: b => fecha(b.fechaLlenado),
    },
    {
      id: 'numeroAnterior',
      titulo: txt('BB # anterior', 'Previous BB #'),
      grupo: G.principal,
      ancho: 110,
      valor: b => textoOGuion(b.numeroAnterior),
    },
    {
      id: 'selloAnterior',
      titulo: txt('Sello anterior', 'Previous seal'),
      grupo: G.principal,
      ancho: 110,
      valor: b => textoOGuion(b.selloAnterior),
    },
    // Columnas del consolidado que todavia no tienen tabla propia.
    {
      id: 'x.invoiceDate',
      titulo: txt('Fecha factura', 'Invoice date'),
      grupo: G.archivo,
      ancho: 120,
      valor: extra('invoiceDate', fecha),
    },
    {
      id: 'x.invoiceYear',
      titulo: txt('Año factura', 'Invoice year'),
      grupo: G.archivo,
      ancho: 90,
      valor: extra('invoiceYear'),
    },
    {
      id: 'x.shipmentNule',
      titulo: 'Shipment Nule',
      grupo: G.archivo,
      ancho: 110,
      valor: extra('shipmentNule'),
    },
    {id: 'x.typeX', titulo: 'TYPE X', grupo: G.archivo, ancho: 90, valor: extra('typeX')},
    {id: 'x.materialType', titulo: 'TYPE', grupo: G.archivo, ancho: 80, valor: extra('materialType')},
    {
      id: 'x.moisturePacked',
      titulo: txt('% humedad', '% moisture'),
      grupo: G.archivo,
      numerica: true,
      ancho: 100,
      valor: extra('moisturePacked', v => numero(v, 2)),
    },
    {
      id: 'x.miningTitle',
      titulo: txt('Título minero', 'Mining title'),
      grupo: G.archivo,
      ancho: 120,
      // El del catalogo, el mismo de los documentos (decision 0038).
      valor: b => b.tituloMinero ?? extra('miningTitle')(b),
    },
    {id: 'x.soldTo', titulo: txt('Vendido a', 'Sold to'), grupo: G.archivo, ancho: 160, valor: extra('soldTo')},
    {id: 'x.origin', titulo: txt('Origen', 'Origin'), grupo: G.archivo, ancho: 130, valor: extra('origin')},
    {
      id: 'x.departure',
      titulo: txt('Salida', 'Departure'),
      grupo: G.archivo,
      ancho: 110,
      valor: extra('departure', fecha),
    },
    {
      id: 'x.arrival',
      titulo: txt('Llegada', 'Arrival'),
      grupo: G.archivo,
      ancho: 110,
      valor: extra('arrival', fecha),
    },
    {
      id: 'x.portOfLoading',
      titulo: txt('Puerto de carga', 'Port of loading'),
      grupo: G.archivo,
      ancho: 130,
      valor: extra('portOfLoading'),
    },
    {id: 'x.eta', titulo: 'ETA', grupo: G.archivo, ancho: 110, valor: extra('eta', fecha)},
    {
      id: 'x.financedByBlq',
      titulo: txt('Financiado BLQ', 'Financed by BLQ'),
      grupo: G.archivo,
      ancho: 110,
      valor: extra('financedByBlq'),
    },
    {
      id: 'x.blDate',
      titulo: txt('Fecha BL', 'BL date'),
      grupo: G.archivo,
      ancho: 110,
      valor: extra('blDate', fecha),
    },
    {
      id: 'x.liquidationDate',
      titulo: txt('Fecha liquidación', 'Settlement date'),
      grupo: G.archivo,
      ancho: 130,
      valor: extra('liquidationDate', fecha),
    },
    {id: 'x.code', titulo: 'CODE', grupo: G.archivo, ancho: 100, valor: extra('code')},
    {
      id: 'x.hindustanArrivalDate',
      titulo: txt('Llegada Hindustan', 'Hindustan arrival'),
      grupo: G.archivo,
      ancho: 130,
      valor: extra('hindustanArrivalDate', fecha),
    },
    {
      id: 'x.ozAuPriceBb',
      titulo: txt('Precio oz Au × BB', 'Au oz price × BB'),
      grupo: G.archivo,
      numerica: true,
      ancho: 130,
      valor: extra('ozAuPriceBb', v => numero(v, 2)),
    },
    {
      id: 'x.ozAgPriceBb',
      titulo: txt('Precio oz Ag × BB', 'Ag oz price × BB'),
      grupo: G.archivo,
      numerica: true,
      ancho: 130,
      valor: extra('ozAgPriceBb', v => numero(v, 2)),
    },
    {
      id: 'x.trm',
      titulo: 'TRM',
      grupo: G.archivo,
      numerica: true,
      ancho: 110,
      valor: extra('trm', v => numero(v, 2)),
    },
    {
      id: 'x.municipioCode',
      titulo: txt('Código municipio', 'Municipality code'),
      grupo: G.archivo,
      ancho: 120,
      valor: extra('municipioCode'),
    },
    {id: 'x.billed', titulo: txt('Facturado', 'Invoiced'), grupo: G.archivo, ancho: 100, valor: extra('billed')},
    {
      id: 'x.entryMonth',
      titulo: txt('Mes de entrada', 'Entry month'),
      grupo: G.archivo,
      ancho: 120,
      valor: extra('entryMonth'),
    },
    {
      id: 'x.auOzPriceToday',
      titulo: txt('Precio oz Au hoy', 'Au oz price today'),
      grupo: G.archivo,
      numerica: true,
      ancho: 120,
      valor: extra('auOzPriceToday', v => numero(v, 2)),
    },
    {
      id: 'x.currentDateMinusOne',
      titulo: txt('Fecha actual −1', 'Current date −1'),
      grupo: G.archivo,
      ancho: 120,
      valor: extra('currentDateMinusOne', fecha),
    },
  ] satisfies ColumnaDeInventario[]
).map(c => ({...c, ayuda: ayudaDeBigBag(c.id)}));

export const GRUPOS_DE_COLUMNAS: GrupoDeColumna[] = [G.principal, G.pesos, G.origen, G.fechas, G.archivo];

export const DEFINICION_INVENTARIO: DefinicionDeColumnas<BigBagResumen, OrdenInventario> = {
  clave: 'cnc.inventario.columnas',
  columnas: COLUMNAS_DE_INVENTARIO,
  grupos: GRUPOS_DE_COLUMNAS,
  fija: 'bbNo',
};
